import { Bold, Italic, Link } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import AIGenerateButton from "./AIGenerateButton";

interface RichTextAreaProps {
  id: string;
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  helpText?: string;
  className?: string;
  // AI generation props
  aiEnabled?: boolean;
  eventContext?: {
    title: string;
    venue: string;
    date: string;
    startTime: string;
    endTime: string;
    price: number;
    creditCost?: number;
    tags: string[];
  };
}

const RichTextArea = ({
  id,
  value,
  onChange,
  label,
  placeholder,
  helpText,
  className = "min-h-[200px] font-mono whitespace-pre-wrap",
  aiEnabled = false,
  eventContext,
}: RichTextAreaProps) => {
  const insertTextAtCursor = (textArea: HTMLTextAreaElement, before: string, after: string, selectedText: string = "") => {
    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const text = selectedText || value.substring(start, end);
    const newText = value.substring(0, start) + before + text + after + value.substring(end);
    onChange(newText);
    
    setTimeout(() => {
      textArea.focus();
      textArea.setSelectionRange(start + before.length, start + before.length + text.length);
    }, 0);
  };

  const handleBoldClick = () => {
    const textArea = document.querySelector(`textarea[id="${id}"]`) as HTMLTextAreaElement;
    if (textArea) {
      insertTextAtCursor(textArea, "<b>", "</b>");
    }
  };

  const handleItalicClick = () => {
    const textArea = document.querySelector(`textarea[id="${id}"]`) as HTMLTextAreaElement;
    if (textArea) {
      insertTextAtCursor(textArea, "<i>", "</i>");
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const isMod = e.metaKey || e.ctrlKey;
    if (isMod && e.key === 'b') {
      e.preventDefault();
      handleBoldClick();
    } else if (isMod && e.key === 'i') {
      e.preventDefault();
      handleItalicClick();
    }
  };

  const handleLinkClick = () => {
    const textArea = document.querySelector(`textarea[id="${id}"]`) as HTMLTextAreaElement;
    if (textArea) {
      const selectedText = value.substring(textArea.selectionStart, textArea.selectionEnd);
      const url = prompt("Enter URL:", "https://");
      if (url) {
        insertTextAtCursor(textArea, `<a href="${url}">`, "</a>", selectedText || "link text");
      }
    }
  };

  const handleAIGenerate = (description: string) => {
    onChange(description);
  };

  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex flex-wrap gap-2 mb-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleBoldClick}
        >
          <Bold className="w-4 h-4 mr-1" />
          Bold
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleItalicClick}
        >
          <Italic className="w-4 h-4 mr-1" />
          Italic
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleLinkClick}
        >
          <Link className="w-4 h-4 mr-1" />
          Add Link
        </Button>
        {aiEnabled && eventContext && (
          <AIGenerateButton
            title={eventContext.title}
            venue={eventContext.venue}
            date={eventContext.date}
            startTime={eventContext.startTime}
            endTime={eventContext.endTime}
            price={eventContext.price}
            creditCost={eventContext.creditCost}
            tags={eventContext.tags}
            existingDescription={value}
            onGenerate={handleAIGenerate}
          />
        )}
      </div>
      <Textarea
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        className={className}
        placeholder={placeholder}
        style={{ 
          fontFamily: 'inherit',
          whiteSpace: 'pre-wrap'
        }}
      />
      {helpText && (
        <p className="text-sm text-muted-foreground">{helpText}</p>
      )}
    </div>
  );
};

export default RichTextArea;
